// [EVAPORATION]/[TEMPERATURE]/[ADJUSTMENTS] are one project-wide climate
// setup, not a list of named objects — this reads/writes 3 flat singleton
// records instead of arrays.
//
// `WINDSPEED FILE` shares the SAME external climate file `[TEMPERATURE]
// FILE` needs, which isn't ported at all — that source round-trips but
// never produces a value (`windSpeedMonthly` stays null).
//
// [ADJUSTMENTS]' per-subcatchment N-PERV/DSTORE/INFIL rows: N-PERV/DSTORE
// locally replace that ONE subcatchment's own PERVIOUS subarea roughness/
// depression-storage adjustment (never IMPERV); INFIL locally REPLACES the
// global CONDUCTIVITY factor for that one subcatchment's own infiltration.
// `ClimatologyEditor.tsx` has no UI for these rows — write*() preserves
// them verbatim.

import { parseNum, parseNumArray } from "./parseNum.ts";
import { writeOrDeleteSection } from "./writeOrDeleteSection.ts";

export type EvapType = "CONSTANT" | "MONTHLY" | "TIMESERIES" | "FILE" | "TEMPERATURE";

export interface Evaporation {
  type: EvapType;
  constantValue: number;
  monthlyValues: number[];
  timeSeries: string;
  /** 12 monthly pan coefficients, or null to use the toolkit's own default (1.0 every month). */
  filePanCoeffs: number[] | null;
  /** Time pattern name for a dry-day evaporation recovery adjustment, or "" for none. */
  recoveryPattern: string;
  dryOnly: boolean;
}

export interface SnowmeltClimatology {
  snowTemp: number;
  tipm: number;
  rnm: number;
  elevation: number;
  latitude: number;
  dtLongitude: number;
}

export type TempSource = "" | "TIMESERIES" | "FILE";

export interface Temperature {
  source: TempSource;
  timeSeries: string;
  fileName: string;
  fileStartDate: string;
  fileUnits: string;
  snowmelt: SnowmeltClimatology | null;
  /** `null` for "not customized" — real project-wide default is all 1.0. */
  adcImpervious: number[] | null;
  adcPervious: number[] | null;
  /** `windSpeedMonthly` stays `null` for the FILE source too — the external file it needs isn't ported. */
  windSpeedSource: "" | "MONTHLY" | "FILE";
  windSpeedMonthly: number[] | null;
}

/** `null` for a field this subcatchment has no override row for. */
export interface AdjustmentSubcatchPattern {
  subcatchment: string;
  nPervPattern: string | null;
  dStorePattern: string | null;
  infilPattern: string | null;
}

export interface Adjustments {
  temperature: number[];
  evaporation: number[];
  rainfall: number[];
  conductivity: number[];
  /** One entry per subcatchment named by an N-PERV/DSTORE/INFIL row — most real projects have none at all. */
  subcatchmentPatterns: AdjustmentSubcatchPattern[];
}

function zeros12(): number[] {
  return new Array(12).fill(0);
}

function ones12(): number[] {
  return new Array(12).fill(1);
}

export function readEvaporation(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): Evaporation {
  const evap: Evaporation = {
    type: "CONSTANT",
    constantValue: 0,
    monthlyValues: zeros12(),
    timeSeries: "",
    filePanCoeffs: null,
    recoveryPattern: "",
    dryOnly: false,
  };
  for (const row of raw.get("EVAPORATION") ?? []) {
    const keyword = row[0]?.toUpperCase() ?? "";
    switch (keyword) {
      case "CONSTANT":
        evap.type = "CONSTANT";
        evap.constantValue = parseNum(row[1], 0, "[EVAPORATION] CONSTANT", warnings);
        break;
      case "MONTHLY":
        evap.type = "MONTHLY";
        evap.monthlyValues = parseNumArray(row.slice(1, 13), 0, "[EVAPORATION] MONTHLY", warnings);
        break;
      case "TIMESERIES":
        evap.type = "TIMESERIES";
        evap.timeSeries = row[1] ?? "";
        break;
      case "FILE":
        evap.type = "FILE";
        evap.filePanCoeffs =
          row.length > 1
            ? parseNumArray(row.slice(1, 13), 1, "[EVAPORATION] FILE", warnings)
            : null;
        break;
      case "TEMPERATURE":
        evap.type = "TEMPERATURE";
        break;
      case "RECOVERY":
        evap.recoveryPattern = row[1] ?? "";
        break;
      case "DRY_ONLY":
        evap.dryOnly = (row[1] ?? "").toUpperCase() === "YES";
        break;
    }
  }
  return evap;
}

export function writeEvaporation(
  raw: ReadonlyMap<string, string[][]>,
  evap: Evaporation
): Map<string, string[][]> {
  const rows: string[][] = [];
  switch (evap.type) {
    case "CONSTANT":
      rows.push(["CONSTANT", String(evap.constantValue)]);
      break;
    case "MONTHLY":
      rows.push(["MONTHLY", ...evap.monthlyValues.map(String)]);
      break;
    case "TIMESERIES":
      rows.push(["TIMESERIES", evap.timeSeries]);
      break;
    case "FILE":
      rows.push(["FILE", ...(evap.filePanCoeffs ? evap.filePanCoeffs.map(String) : [])]);
      break;
    case "TEMPERATURE":
      rows.push(["TEMPERATURE"]);
      break;
  }
  if (evap.recoveryPattern) rows.push(["RECOVERY", evap.recoveryPattern]);
  rows.push(["DRY_ONLY", evap.dryOnly ? "YES" : "NO"]);
  const next = new Map(raw);
  next.set("EVAPORATION", rows);
  return next;
}

const TEMPERATURE_KNOWN_KEYWORDS = new Set(["TIMESERIES", "FILE", "SNOWMELT", "ADC", "WINDSPEED"]);

export function readTemperature(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): Temperature {
  const temp: Temperature = {
    source: "",
    timeSeries: "",
    fileName: "",
    fileStartDate: "",
    fileUnits: "",
    snowmelt: null,
    adcImpervious: null,
    adcPervious: null,
    windSpeedSource: "",
    windSpeedMonthly: null,
  };
  for (const row of raw.get("TEMPERATURE") ?? []) {
    const keyword = row[0]?.toUpperCase() ?? "";
    switch (keyword) {
      case "TIMESERIES":
        temp.source = "TIMESERIES";
        temp.timeSeries = row[1] ?? "";
        break;
      case "FILE":
        temp.source = "FILE";
        temp.fileName = row[1] ?? "";
        temp.fileStartDate = !row[2] || row[2] === "*" ? "" : row[2];
        temp.fileUnits = row[3] ?? "";
        break;
      case "SNOWMELT":
        temp.snowmelt = {
          snowTemp: parseNum(row[1], 0, "[TEMPERATURE] SNOWMELT Snow Temp", warnings),
          // Real engine defaults (0.5/0.6), applied when the row omits
          // these tokens rather than silently zeroing them.
          tipm: parseNum(row[2], 0.5, "[TEMPERATURE] SNOWMELT TIPM", warnings),
          rnm: parseNum(row[3], 0.6, "[TEMPERATURE] SNOWMELT RNM", warnings),
          elevation: parseNum(row[4], 0, "[TEMPERATURE] SNOWMELT Elevation", warnings),
          latitude: parseNum(row[5], 0, "[TEMPERATURE] SNOWMELT Latitude", warnings),
          dtLongitude: parseNum(row[6], 0, "[TEMPERATURE] SNOWMELT DT Longitude", warnings),
        };
        break;
      case "ADC": {
        // Prefix-matched, not exact — a real file can (and commonly does)
        // abbreviate to just IMPERV/PERV.
        const which = (row[1] ?? "").toUpperCase();
        const values = parseNumArray(row.slice(2, 12), 1, `[TEMPERATURE] ADC ${which}`, warnings);
        if (which.startsWith("IMPERV")) temp.adcImpervious = values;
        else if (which.startsWith("PERV")) temp.adcPervious = values;
        break;
      }
      case "WINDSPEED":
        if ((row[1] ?? "").toUpperCase() === "FILE") {
          temp.windSpeedSource = "FILE";
        } else {
          temp.windSpeedSource = "MONTHLY";
          temp.windSpeedMonthly = parseNumArray(
            row.slice(2, 14),
            0,
            "[TEMPERATURE] WINDSPEED MONTHLY",
            warnings
          );
        }
        break;
    }
  }
  return temp;
}

export function writeTemperature(
  raw: ReadonlyMap<string, string[][]>,
  temp: Temperature
): Map<string, string[][]> {
  const preserved = (raw.get("TEMPERATURE") ?? []).filter(
    (row) => !TEMPERATURE_KNOWN_KEYWORDS.has(row[0]?.toUpperCase() ?? "")
  );
  const rows: string[][] = [];
  if (temp.source === "TIMESERIES") rows.push(["TIMESERIES", temp.timeSeries]);
  else if (temp.source === "FILE") {
    rows.push(["FILE", temp.fileName, temp.fileStartDate || "*", temp.fileUnits || "F"]);
  }
  if (temp.snowmelt) {
    const s = temp.snowmelt;
    rows.push([
      "SNOWMELT",
      String(s.snowTemp),
      String(s.tipm),
      String(s.rnm),
      String(s.elevation),
      String(s.latitude),
      String(s.dtLongitude),
    ]);
  }
  if (temp.adcImpervious) rows.push(["ADC", "IMPERVIOUS", ...temp.adcImpervious.map(String)]);
  if (temp.adcPervious) rows.push(["ADC", "PERVIOUS", ...temp.adcPervious.map(String)]);
  if (temp.windSpeedSource === "FILE") rows.push(["WINDSPEED", "FILE"]);
  else if (temp.windSpeedSource === "MONTHLY") {
    rows.push([
      "WINDSPEED",
      "MONTHLY",
      ...(temp.windSpeedMonthly ?? new Array(12).fill(0)).map(String),
    ]);
  }
  rows.push(...preserved);
  return writeOrDeleteSection(raw, "TEMPERATURE", rows);
}

const ADJUSTMENTS_KNOWN_PREFIXES = ["TEMP", "EVAP", "RAIN", "COND"];

/**
 * TEMPERATURE/EVAPORATION rows are ADDITIVE offsets (default 0.0);
 * RAINFALL/CONDUCTIVITY rows are MULTIPLICATIVE factors (default 1.0) —
 * defaulting all 4 to 0 would zero out every rainfall/conductivity value
 * outright the moment a consumer multiplies by it, instead of leaving it
 * unchanged as an absent row must.
 */
export function readAdjustments(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): Adjustments {
  const adjustments: Adjustments = {
    temperature: zeros12(),
    evaporation: zeros12(),
    rainfall: ones12(),
    conductivity: ones12(),
    subcatchmentPatterns: [],
  };
  const bySubcatch = new Map<string, AdjustmentSubcatchPattern>();
  function entryFor(name: string): AdjustmentSubcatchPattern {
    let entry = bySubcatch.get(name);
    if (!entry) {
      entry = { subcatchment: name, nPervPattern: null, dStorePattern: null, infilPattern: null };
      bySubcatch.set(name, entry);
      adjustments.subcatchmentPatterns.push(entry);
    }
    return entry;
  }
  for (const row of raw.get("ADJUSTMENTS") ?? []) {
    const keyword = row[0]?.toUpperCase() ?? "";
    if (keyword.startsWith("TEMP")) {
      adjustments.temperature = parseNumArray(
        row.slice(1, 13),
        0,
        "[ADJUSTMENTS] TEMPERATURE",
        warnings
      );
    } else if (keyword.startsWith("EVAP")) {
      adjustments.evaporation = parseNumArray(
        row.slice(1, 13),
        0,
        "[ADJUSTMENTS] EVAPORATION",
        warnings
      );
    } else if (keyword.startsWith("RAIN")) {
      adjustments.rainfall = parseNumArray(row.slice(1, 13), 1, "[ADJUSTMENTS] RAINFALL", warnings);
    } else if (keyword.startsWith("COND")) {
      adjustments.conductivity = parseNumArray(
        row.slice(1, 13),
        1,
        "[ADJUSTMENTS] CONDUCTIVITY",
        warnings
      );
    }
    // Per-subcatchment pattern-override rows: `keyword subcatchID
    // patternID`, both names resolved elsewhere.
    else if (keyword.startsWith("N-PERV") && row[1] && row[2])
      entryFor(row[1]).nPervPattern = row[2];
    else if (keyword.startsWith("DSTORE") && row[1] && row[2])
      entryFor(row[1]).dStorePattern = row[2];
    else if (keyword.startsWith("INFIL") && row[1] && row[2])
      entryFor(row[1]).infilPattern = row[2];
  }
  return adjustments;
}

export function writeAdjustments(
  raw: ReadonlyMap<string, string[][]>,
  adjustments: Adjustments
): Map<string, string[][]> {
  const preserved = (raw.get("ADJUSTMENTS") ?? []).filter((row) => {
    const keyword = row[0]?.toUpperCase() ?? "";
    return !ADJUSTMENTS_KNOWN_PREFIXES.some((prefix) => keyword.startsWith(prefix));
  });
  const isAllDefault =
    adjustments.temperature.every((v) => v === 0) &&
    adjustments.evaporation.every((v) => v === 0) &&
    adjustments.rainfall.every((v) => v === 1) &&
    adjustments.conductivity.every((v) => v === 1);
  const rows: string[][] = [];
  if (!isAllDefault || preserved.length > 0) {
    rows.push(["TEMPERATURE", ...adjustments.temperature.map(String)]);
    rows.push(["EVAPORATION", ...adjustments.evaporation.map(String)]);
    rows.push(["RAINFALL", ...adjustments.rainfall.map(String)]);
    rows.push(["CONDUCTIVITY", ...adjustments.conductivity.map(String)]);
  }
  rows.push(...preserved);
  return writeOrDeleteSection(raw, "ADJUSTMENTS", rows);
}
